import { getApiUrl } from './config';

/**
 * Extract CID from IPFS URI
 */
export const extractCid = (uri: string): string => {
  if (!uri) return '';
  if (uri.startsWith('ipfs://')) {
    return uri.replace('ipfs://', '');
  }
  // Already a CID or HTTP URL
  return uri;
};

/**
 * Convert IPFS URI to HTTP URL using our proxy API
 * This provides: disk caching, multi-gateway fallback, and consistent CORS headers
 */
export const ipfsToHttp = (uri: string): string => {
  if (!uri) return '';

  // If it's already an HTTP URL, return as is
  if (uri.startsWith('http://') || uri.startsWith('https://')) {
    return uri;
  }

  // If it's a local path (starts with /), return as is
  if (uri.startsWith('/')) {
    return uri;
  }

  const cid = extractCid(uri);

  // Always use the API proxy for fetching IPFS content
  // The API handles caching, multi-gateway fallback, and R2 CDN
  return getApiUrl(`/api/img/${cid}`);
};

/**
 * Size presets for image processor
 */
export type ImagePreset = 'thumb' | 'thumb-2x' | 'small' | 'medium' | 'large' | 'full';
export type ImageFormat = 'webp' | 'jpeg';

/**
 * Get optimized image URL from the image processor
 * Uses Sharp for resizing and WebP conversion
 *
 * @param uri - IPFS URI or CID
 * @param preset - Size preset (thumb=128, small=256, medium=512, large=1024, full=2048)
 * @param format - Output format (webp or jpeg)
 */
export const getOptimizedImageUrl = (
  uri: string,
  preset: ImagePreset = 'medium',
  format: ImageFormat = 'webp'
): string => {
  if (!uri) return '';

  // If it's already an HTTP URL from a known CDN, return as is
  if (uri.startsWith('http://') || uri.startsWith('https://')) {
    // Could transform CDN URLs too, but for now pass through
    return uri;
  }

  const cid = extractCid(uri);
  if (!cid) return '';

  // Use image processor service
  return getApiUrl(`/api/img/${cid}/${preset}.${format}`);
};

/**
 * Get thumbnail URL (128px WebP)
 */
export const getThumbnailUrl = (uri: string): string => {
  return getOptimizedImageUrl(uri, 'thumb', 'webp');
};

/**
 * Get card image URL (256px WebP) - good for NFT grids
 * Note: This returns static WebP (first frame for animated GIFs)
 */
export const getCardImageUrl = (uri: string): string => {
  return getOptimizedImageUrl(uri, 'small', 'webp');
};

/**
 * Get animated thumbnail URL that preserves all animation frames
 * Uses the /api/anim endpoint which:
 * - Preserves animation (keeps every other frame, doubles delays)
 * - Converts to animated WebP (significantly smaller than GIF)
 * - Returns static WebP if source is not animated
 *
 * @param uri - IPFS URI or CID
 * @param size - Desired size (will snap to nearest allowed: 64, 128, 256)
 */
export const getAnimatedThumbnailUrl = (
  uri: string,
  size: number = 256
): string => {
  if (!uri) return '';

  // If it's already an HTTP URL, return as is
  if (uri.startsWith('http://') || uri.startsWith('https://')) {
    return uri;
  }

  const cid = extractCid(uri);
  if (!cid) return '';

  // Use the animated WebP endpoint
  return getApiUrl(`/api/anim/${cid}?w=${size}`);
};

/**
 * Get detail image URL (512px WebP) - good for NFT detail pages
 */
export const getDetailImageUrl = (uri: string): string => {
  return getOptimizedImageUrl(uri, 'medium', 'webp');
};

/**
 * Get full size image URL (2048px WebP) - for lightbox/fullscreen
 */
export const getFullImageUrl = (uri: string): string => {
  return getOptimizedImageUrl(uri, 'full', 'webp');
};

export const fetchIPFSMetadata = async (uri: string): Promise<any> => {
  try {
    const httpUrl = ipfsToHttp(uri);
    console.log('[fetchIPFSMetadata] uri:', uri, '-> httpUrl:', httpUrl);
    const response = await fetch(httpUrl);
    if (!response.ok) {
      console.error('[fetchIPFSMetadata] Failed:', response.status, response.statusText);
      throw new Error('Failed to fetch metadata');
    }
    const json = await response.json();
    console.log('[fetchIPFSMetadata] Success:', json?.name, 'image:', json?.displayUri || json?.image);
    return json;
  } catch (error) {
    console.error('[fetchIPFSMetadata] Error for', uri, ':', error);
    return null;
  }
};

// Optional: Upload to IPFS using Pinata (requires API keys)
export const uploadToPinata = async (file: File): Promise<string | null> => {
  const apiKey = import.meta.env.VITE_PINATA_API_KEY;
  const secretKey = import.meta.env.VITE_PINATA_SECRET_KEY;

  if (!apiKey || !secretKey) {
    console.error('Pinata API keys not configured');
    return null;
  }

  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch('https://api.pinata.cloud/pinning/pinFileToIPFS', {
      method: 'POST',
      headers: {
        'pinata_api_key': apiKey,
        'pinata_secret_api_key': secretKey,
      },
      body: formData,
    });

    if (!response.ok) throw new Error('Failed to upload to Pinata');

    const data = await response.json();
    return `ipfs://${data.IpfsHash}`;
  } catch (error) {
    console.error('Error uploading to Pinata:', error);
    return null;
  }
};
